import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ChevronDown, ChevronUp, Droplets, Pencil, Plus, Trash2 } from "lucide-react";
import { useGetIrrigationMethod, useGetPowerSource } from "../../../../api/static-data";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { CULTIVATION_TYPE } from "../../../../config/constants";
import { formatNumber, isCheckPercentageIrrigation, validateCultivationNumber, validatePercentageNumber } from "../../../../utils/utils";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { Button } from "../../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import CultivarSaveReminder from "./CultivarSaveReminder";
import { useCultivarSectionAccordion } from "../hooks/useCultivarSectionAccordion";

const Irrigation = forwardRef(
  ({ currentFormData, currentDataType, lockStatus = false, sectionsDisabled = false }: any, ref) => {
    const { accordionEnabled, toggleAccordion, headerClassName } = useCultivarSectionAccordion(
      sectionsDisabled
    );
    const [isEditingEntry, setIsEditingEntry] = useState(false);
    const [formEntryKey, setFormEntryKey] = useState(0);
    const [irrigationListData, setIrrigationListData] = useState<any>([]);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
    const [deleteValue, setDeleteValue] = useState<any>({
      item: "",
      type: ""
    });

    const { data: irrigationData } = useGetIrrigationMethod();

    const { data: powerSourceData } = useGetPowerSource();

    const irrigationDetails = useCallback(() => {
      const irrigationTableData =
        currentFormData?.cultivationField?.irrigations?.map((item: any) => {
          return {
            ...item,
            id: parseInt(item.id)
          };
        }) ?? [];
      setIrrigationListData(irrigationTableData);
    }, [currentFormData]);

    useEffect(() => {
      irrigationDetails();
    }, [currentFormData]);

    const emptyFormValues = useMemo(
      () => ({
        waterAmount: "",
        appliedPortion: "",
        irrigation: "",
        powerSource: ""
      }),
      []
    );

    const form = useForm({
      defaultValues: emptyFormValues,
      mode: "all",
      reValidateMode: "onChange"
    });
    const { control, handleSubmit, reset, clearErrors } = form;

    const resetIrrigationForm = useCallback(() => {
      clearErrors();
      reset(emptyFormValues);
      setIsEditingEntry(false);
      setEditIndex(null);
      setFormEntryKey(key => key + 1);
    }, [clearErrors, emptyFormValues, reset]);

    const addIrrigation = handleSubmit(formValues => {
      const formattedData = {
        irrigationMethodId: Number(formValues.irrigation),
        powerSourceId: Number(formValues.powerSource),
        waterAmount: Number(formValues.waterAmount),
        appliedPortion: Number(formValues.appliedPortion)
      };

      if (isEditingEntry && editIndex !== null) {
        const updatedList = [...(irrigationListData ?? [])];
        updatedList[editIndex] = {
          ...updatedList[editIndex],
          ...formattedData
        };
        setIrrigationListData(updatedList);
      } else {
        setIrrigationListData([...(irrigationListData ?? []), formattedData]);
      }
      resetIrrigationForm();
    });

    const onEditHandler = (item: any, index: number) => {
      setIsEditingEntry(true);
      setEditIndex(index);
      reset({
        waterAmount: item?.waterAmount?.toString() ?? "",
        appliedPortion: item?.appliedPortion?.toString() ?? "",
        irrigation: item.irrigationMethodId.toString(),
        powerSource: item.powerSourceId.toString()
      });
      setFormEntryKey(key => key + 1);
    };

    const deleteIrrigation = useCallback(() => {
      setIrrigationListData(
        (irrigationListData ?? []).filter((_: any, index: number) => index !== deleteValue.index)
      );
      setVisibleDeleteModal(false);
      setDeleteValue({});
    }, [irrigationListData, deleteValue]);

    useImperativeHandle(ref, () => ({
      getIrrigationData: () => {
        return irrigationListData;
      }
    }));

    const handleClick = toggleAccordion;

    return currentDataType === CULTIVATION_TYPE.Supplier ? (
      <Card className="tw-border-border tw-p-0 tw-gap-0">
        <CardHeader className={headerClassName} onClick={handleClick}>
          <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
            <div className="tw-flex tw-items-center tw-gap-2"><Droplets className="tw-h-4 tw-w-4 tw-text-primary" /><span className="tw-text-sm tw-font-semibold tw-text-foreground">Irrigation</span></div>
            {accordionEnabled ? <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" /> : <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />}
          </div>
        </CardHeader>
        <CardContent
          className={`tw-py-3.5 tw-px-3.5 tw-space-y-4 ${accordionEnabled ? "" : "tw-hidden"}`}
        >
            <Form {...form}>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-[1fr_1fr_1fr_1fr_auto] tw-items-start tw-gap-4">
                <FormSelect
                  control={control}
                  name="irrigation"
                  label="Irrigation Method"
                  placeholder="Select Irrigation Method"
                  options={irrigationData ?? []}
                  resetKey={formEntryKey}
                  required
                  errorMessage="Irrigation method is required"
                />
                <FormSelect
                  control={control}
                  name="powerSource"
                  label="Power Source"
                  placeholder="Select Power Source"
                  options={powerSourceData ?? []}
                  resetKey={formEntryKey}
                  required
                  errorMessage="Power source is required"
                />
                <FormInputNumber control={control} label="Water Amount (m3)" placeholder="Water Amount (m3)" name="waterAmount" required errorMessage="Water Amount is required" rules={{ validate: { valueCheck: (v: any) => validateCultivationNumber(v) } }} />
                <FormInputNumber control={control} label="Applied Portion ( % )" placeholder="Applied Portion ( % )" name="appliedPortion" required errorMessage="Applied Portion is required" rules={{ validate: { valueCheck: (v: any) => validatePercentageNumber(v) } }} />
                <div className="tw-space-y-2 tw-mt-2">
                  <div
                    className="tw-text-sm tw-font-medium tw-leading-none tw-text-foreground tw-opacity-0 tw-select-none"
                    aria-hidden
                  >
                    Add
                  </div>
                  <Button
                    type="button"
                    size="lg"
                    className={`!tw-h-10 ${isEditingEntry ? "" : "!tw-w-10 !tw-px-0"}`}
                    disabled={lockStatus}
                    onClick={addIrrigation}
                  >
                    {isEditingEntry ? "Update" : <Plus className="tw-h-4 tw-w-4" />}
                  </Button>
                </div>
              </div>
            </Form>
            {irrigationListData?.length > 0 && (
              <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="tw-border-b tw-border-border hover:tw-bg-transparent">
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Irrigation Method
                      </TableHead>
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Power Source
                      </TableHead>
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Water Amount (m3)
                      </TableHead>
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Applied Portion (%)
                      </TableHead>
                      {!lockStatus && (
                        <TableHead
                          className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground tw-text-right tw-w-24"
                          style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                        >
                          Actions
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {irrigationListData?.map((item: any, index: any) => (
                      <TableRow key={`irrigation_${item?.id ?? index}`} className="hover:tw-bg-muted/20">
                        <TableCell className="tw-px-4 tw-py-3 tw-text-sm">
                          {irrigationData?.find((e: any) => e.value === String(item?.irrigationMethodId))
                            ?.text ?? ""}
                        </TableCell>
                        <TableCell className="tw-px-4 tw-py-3 tw-text-sm">
                          {powerSourceData?.find((e: any) => e.value === String(item?.powerSourceId))
                            ?.text ?? ""}
                        </TableCell>
                        <TableCell className="tw-px-4 tw-py-3 tw-text-sm tw-font-mono tw-text-muted-foreground">
                          {formatNumber(item?.waterAmount)}
                        </TableCell>
                        <TableCell className="tw-px-4 tw-py-3 tw-text-sm tw-font-mono tw-text-muted-foreground">
                          {item?.appliedPortion ?? ""}
                        </TableCell>
                        {!lockStatus && (
                          <TableCell className="tw-px-4 tw-py-3">
                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-0.5">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="tw-h-7 tw-w-7 tw-p-0"
                                onClick={() => {
                                  onEditHandler(item, index);
                                }}
                              >
                                <Pencil className="tw-h-3.5 tw-w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="tw-h-7 tw-w-7 tw-p-0 tw-text-destructive hover:tw-text-destructive"
                                onClick={() => {
                                  setVisibleDeleteModal(true);
                                  setDeleteValue({ type: "Irrigation", item, index });
                                }}
                              >
                                <Trash2 className="tw-h-3.5 tw-w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <CultivarSaveReminder section="irrigation" hidden={lockStatus} />
            <ConfirmModal
            viewModal={visibleDeleteModal}
            closeModal={() => setVisibleDeleteModal(false)}
            cancel={() => {
              setVisibleDeleteModal(false);
              setDeleteValue({});
            }}
            approve={deleteIrrigation}
            title={`Delete ${deleteValue.type}`}
            subTitle={`Are you sure you want to delete Irrigation data?`}
            />
          </CardContent>
      </Card>
    ) : null;
  }
);

export default Irrigation;
